<script setup lang="ts">
import { Address, DefenceQuestion, useApplicationStore, ApplyPageStages } from "@/stores/application";
import { Input, Question, RadioSet, Radio } from "./fields";
import { applicationDetailsValidator } from "@/utils/validators";
import { tooltips } from "@/tooltips/yourDetailsTooltips";
import { isNumber, toTitleCase } from "@/utils";
import { format } from "date-fns";
import DefenceLineQuestion from "./DefenceLineQuestion.vue";
import AddressSuggestion from "./AddressSuggestion.vue";
import { useQuoteStore } from "@/stores/quotes";
import { computed } from "vue";
import { LINKS } from "@/constants/links";

const applicationStore = useApplicationStore();
const quoteStore = useQuoteStore();

const validator = applicationDetailsValidator(applicationStore);

const firstApplicant = computed(() => applicationStore.applicationStage === ApplyPageStages.AboutYou);

const aboutYouInfo = computed(() => applicationStore.applicantAboutDetails);

const applicantAllowanceProtection = computed<DefenceQuestion>(() => ({
    name: "applicantAllowanceProtection",
    label: `Do you have any protection in place against the iLSA tax charge
                (or do you have any existing lifetime allowance protection)?`,
    errorLabel: "below standard lifetime allowance",
    value: aboutYouInfo.value.applicantAllowanceProtection,
}));

const applicantLifetimeAllowanceTaxProtection = computed<DefenceQuestion>(() => ({
    name: "applicantLifetimeAllowanceTaxProtection",
    label: "If you have requested any tax-free lump sum will this exceed your iLSA?",
    errorLabel: "researched",
    value: aboutYouInfo.value.applicantLifetimeAllowanceTaxProtection,
}));

const setDoctorsAddress = (address: Address) => {
    aboutYouInfo.value.doctorsDetails = {
        ...aboutYouInfo.value.doctorsDetails,
        ...address,
    };
};

</script>
<template>
    <div
        :key="applicationStore.applicationStage"
        class="row"
    >
        <div class="col-12">
            <div class="card">
                <div class="card-header-1">
                    <div class="font-weight-bold-col mt-2">
                        {{ firstApplicant ? "About you" : "About the named spouse/civil partner/financial dependant" }}
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body-new pb-0">
                    <div class="yourDetails">
                        <p class="font-weight-bold-col">
                            {{ firstApplicant ? "Your details" : "Dependant details" }}
                        </p>
                        <p class="alert alert-info">
                            {{ firstApplicant ? "You should check the information below and complete the details still required." :
                                "These questions should be answered by the named spouse, registered civil partner " +
                                "or financially dependent partner. (referred to as 'dependant')" }}
                        </p>
                        <Question
                            name="title"
                            label="Title"
                            input-class="col-md-8 static-input"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <p class="sessioncamhidetext">
                                {{ toTitleCase(aboutYouInfo.title ?? "") }}
                            </p>
                        </Question>
                        <Question
                            type="group"
                            name="fullName"
                            label="Full name"
                            :tooltip="tooltips.fullNameApplication"
                            input-class="col-md-8"
                            label-class="order-first col-md-2 col-form-label pt-2"
                        >
                            <Input
                                id="forenameMulti"
                                v-model="aboutYouInfo.forename"
                                hidden-label="Forename"
                                placeholder="Forename"
                                type="text"
                                :validation="validator.forename"
                            />
                            <Input
                                id="surnameMulti"
                                v-model="aboutYouInfo.surname"
                                hidden-label="Surname"
                                placeholder="Surname"
                                type="text"
                                :validation="validator.surname"
                            />
                        </Question>
                        <Question
                            name="dob"
                            label="Date of birth"
                            input-class="col-md-8 static-input"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <p class="sessioncamhidetext">
                                {{ format(new Date(aboutYouInfo.dob), 'dd/MM/yyyy') }}
                            </p>
                        </Question>
                        <Question
                            name="maritalStatus"
                            label="Marital status"
                            input-class="col-md-8 static-input"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <p>{{ aboutYouInfo.maritalStatus ?? "" }}</p>
                        </Question>

                        <Question
                            name="nationalInsuranceNumber"
                            :label="`${firstApplicant ? 'Your' : ''} National Insurance number`"
                            :tooltip="`Please enter your permanent National Insurance number;
                                 for example AB123456C. Temporary numbers will not be accepted.`"
                            input-class="col-md-8"
                            label-class="order-first col-md-2 col-form-label pt-1"
                        >
                            <Input
                                id="nationalInsuranceNumber"
                                v-model="aboutYouInfo.nationalInsuranceNo"
                                placeholder="National Insurance number"
                                type="text"
                                :validation="validator.nationalInsurance"
                                class="col-md"
                            />
                        </Question>
                    </div>
                    <hr>
                    <div class="yourAddress">
                        <p class="font-weight-bold-col">
                            {{ firstApplicant ? 'Your' : 'Dependant' }} address
                        </p>
                        <p class="alert alert-info">
                            This address should be at the same postcode you used for your quote. If this is not the same your quotes may be invalid.
                        </p>
                        <AddressSuggestion
                            :address="aboutYouInfo.address"
                            :postcode-verify="quoteStore.quote?.customer.address.postcode ?? undefined"
                            :question-row="true"
                            :find-address-label="firstApplicant ? 'Find Your Address' : 'Find Their Address'"
                            id-prepend="applicant"
                            @address-update="(value: Address) => aboutYouInfo.address = value"
                        />
                    </div>
                    <hr>
                    <div class="yourContactDetails">
                        <p class="font-weight-bold-col">
                            {{ firstApplicant ? 'Your' : 'Dependant' }} contact details
                        </p>
                        <Question
                            v-if="firstApplicant"
                            name="email"
                            label="Email address"
                            input-class="col-md-8 static-input"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <p class="sessioncamhidetext">
                                {{ aboutYouInfo.email }}
                            </p>
                        </Question>
                        <Question
                            :name="firstApplicant ? 'yourPhone' : 'dependantPhone'"
                            label="Telephone number"
                            input-class="col-md-8"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <Input
                                :id="firstApplicant ? 'yourPhone' : 'dependantPhone'"
                                v-model="aboutYouInfo.phone"
                                placeholder="Telephone number"
                                inputmode="numeric"
                                type="text"
                                :validation="validator.phone"
                            />
                        </Question>
                    </div>
                    <hr>
                    <div
                        v-if="firstApplicant"
                        class="yourContactDetails"
                    >
                        <p class="font-weight-bold-col">
                            Individual Lump Sum Allowance
                        </p>
                        <p class="alert alert-info">
                            The individual Lump Sum Allowance (iLSA) is set by the government. It limits the amount of tax-free lump sum you can take from all of your pension funds. The maximum is £268,275 minus the value of any tax-free lump sum you intend to take, have already taken, or want to take at the same time. If you have any type of lifetime allowance (LTA) protection, you may be entitled to a higher iLSA. Annuity providers require the following information to report to HMRC.
                        </p>
                        <DefenceLineQuestion
                            :question="applicantAllowanceProtection"
                            :question-row="true"
                            @value-change="(value) => aboutYouInfo.applicantAllowanceProtection = value"
                        />
                        <p
                            v-if="aboutYouInfo.applicantAllowanceProtection"
                            class="alert alert-info"
                        >
                            We will need a copy of the protection you have in place. Please send this to <a href="mailto:hello@annuityready.com">hello@annuityready.com </a> along with your quote number after you have submitted your application.
                        </p>
                        <DefenceLineQuestion
                            :question="applicantLifetimeAllowanceTaxProtection"
                            :question-row="true"
                            @value-change="(value) => aboutYouInfo.applicantLifetimeAllowanceTaxProtection = value"
                        />
                        <Question
                            v-if="aboutYouInfo.applicantLifetimeAllowanceTaxProtection"
                            name="applicantRemainingIlsa"
                            label="Your annuity provider will not be able to pay any tax-free
                             lump sum that exceeds your iLSA. Please confirm the amount of iLSA you have remaining:"
                        >
                            <Input
                                v-model.number="aboutYouInfo.applicantRemainingIlsa"
                                inputmode="text"
                                type="currency"
                                placeholder="0"
                                :validation="validator.applicantRemainingIlsa"
                                :filter="isNumber"
                            />
                        </Question>
                    </div>
                    <hr v-if="firstApplicant">
                    <div class="doctorDetails">
                        <p class="font-weight-bold-col">
                            {{ firstApplicant ? 'Your' : 'Dependant' }} doctor’s details
                        </p>
                        <p
                            v-if="firstApplicant"
                            class="alert alert-info"
                        >
                            Your chosen annuity provider will require your doctor's details. We will send you a medical application for you to sign and return. This will give the provider consent to check your medical information with your doctor.
                            <br><br>
                            The annuity provider will aim to start your annuity payment as soon as possible after receiving your pension funds. In some cases, they may request a report from your doctor after your income starts to check any medical and/or lifestyle information you have provided. If they find the report is different from the information you have provided and you have been paid too much, they may reduce your income and recover any overpayments made to you.
                        </p>
                        <p
                            v-else
                            class="alert alert-info"
                        >
                            The annuity provider will require your doctor's details. We will send you an annuity health form for you and the annuity holder to sign and return. This will give the provider consent to check your medical information with their doctor.
                            <br><br>
                            The annuity provider will aim to commence the annuity payment as soon as possible after receiving the pension funds. In some cases, they   may request a report from your doctor after your income starts to check any medical and/or lifestyle information you have provided. If they find the report is different from the information you have provided and you have been paid too much, they may reduce the annuity income and recover any overpayments made.
                        </p>
                        <Question
                            name="doctorName"
                            label="Doctor's surgery / doctor's name"
                            input-class="col-md-8 static-input"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <Input
                                v-model="aboutYouInfo.doctorsDetails.name"
                                placeholder="Doctor surgery / Doctor name"
                                inputmode="text"
                                type="text"
                                class="col-md"
                                :validation="validator.doctorsName"
                            />
                        </Question>
                        <AddressSuggestion
                            :address="aboutYouInfo.doctorsDetails"
                            :question-row="true"
                            :find-address-label="firstApplicant ? `Find Your Doctor's Address` : `Find Their Doctor's Address`"
                            id-prepend="doctor"
                            @address-update="(value: Address) => setDoctorsAddress(value)"
                        />
                        <Question
                            name="doctorPhone"
                            label="Telephone number"
                            input-class="col-md-8"
                            label-class="order-first col-md-2 col-form-label pt-0"
                        >
                            <Input
                                id="doctorPhone"
                                v-model="aboutYouInfo.doctorsDetails.phone"
                                placeholder="Telephone number"
                                inputmode="numeric"
                                type="text"
                                :validation="validator.phone"
                            />
                        </Question>
                    </div>
                    <hr>
                    <div class="sensitiveInformation">
                        <p class="font-weight-bold-col">
                            Sensitive information
                        </p>
                        <p class="alert alert-info">
                            By completing this application, you agree to Annuity Ready passing your application and the medical, health and lifestyle information provided in this form and any other medical information provided in the course of this application to {{ applicationStore.companyName }} for the purposes of allowing them to underwrite and administer your policy.
                            <br><br>
                            Under the Access to Medical Reports Act 1988, you have the following rights:
                            <br><br>
                            <ul>
                                <li class="pt-2">
                                    To withhold your consent for a medical report to be sent to {{ applicationStore.companyName }}, although if you withhold this consent then {{ applicationStore.companyName }} will be unable to accept your application.
                                </li>
                                <li class="pt-2">
                                    If you provide consent to a doctor's report, you have the right to see it before the report is sent to {{ applicationStore.companyName }}. You have 21 days to contact your doctor to arrange to see the report and the doctor must obtain your further consent before it is sent on to {{ applicationStore.companyName }}. If you do not arrange to see the report within 21 days, it will be sent to {{ applicationStore.companyName }}. You will retain the right to see the report at any time within six months of it being sent to {{ applicationStore.companyName }}.
                                </li>
                                <li class="pt-2">
                                    To request amendments to be made to the report. If your doctor refuses to make the amendments, you have the right to request that the doctor attaches a statement containing your views on the report.
                                </li>
                            </ul>
                            <br><br>
                            The doctor does not have to let you see any part of a report that they consider would be likely to cause serious harm to your physical or mental health or to that of others or would indicate the doctor's intentions towards you. The doctor also does not have to let you see any part of a report which may disclose the identity of another person who has supplied information about you, unless that person has consented or is a health professional caring for you. If the doctor does not let you see any part of a report, they must notify you of that fact.
                        </p>
                        <p>
                            By clicking ‘continue to apply’, I confirm that I have been informed of my rights under the Access to Medical Reports Act 1988 and consent to {{ applicationStore.companyName }} seeking medical information concerning my physical or mental health from any doctor who has attended me at any time. I agree to the release of this information to {{ applicationStore.companyName }}.
                        </p>
                        <Question
                            name="medialReport"
                            class="mt-4"
                            :label="`Do you want to see the medical report before it is sent to ${applicationStore.companyName}?`"
                            input-class="col-md-5"
                            label-class="order-first col-md-6 col-form-label pt-0"
                        >
                            <RadioSet>
                                <Radio
                                    v-model="aboutYouInfo.seeTheMedicalReport"
                                    title="Yes"
                                    :value="true"
                                    :validation="validator.seeMedicalReport"
                                />
                                <Radio
                                    v-model="aboutYouInfo.seeTheMedicalReport"
                                    title="No"
                                    :value="false"
                                    :validation="validator.seeMedicalReport"
                                />
                            </RadioSet>
                        </Question>
                        <p class="mb-2">
                            By clicking the ‘Continue to apply’ button, {{ firstApplicant ? 'I' : 'we' }} consent to
                            Annuity Ready processing the information including lifestyle, health and medical details
                            that {{ firstApplicant ? 'I' : 'we' }} have provided or will provide, in accordance with
                            the Annuity Ready <a
                                :href="LINKS.ANNUITY_TERMS_AND_CONDITIONS"
                                onclick="window.open(this.href, '',
                'resizable=no,status=no,location=no,toolbar=no,menubar=no,fullscreen=no,scrollbars=yes,dependent=no,width=900,height=650');
                return false;"
                            >terms &amp; conditions</a>
                            and
                            <a
                                :href="LINKS.ANNUITY_PRIVACY_POLICY"
                                onclick="window.open(this.href, '',
                'resizable=no,status=no,location=no,toolbar=no,menubar=no,fullscreen=no,scrollbars=yes,dependent=no,width=900,height=650');
                return false;"
                            >privacy notice</a> which include details of providers they may share the information with to obtain annuity quotes.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.static-input {
    font-weight: 500;
}
</style>
